import { useUserStore } from '@/lib/stores'
import { accessMapCache } from '@/lib/utils/shell'
import { match } from 'path-to-regexp'
import { navigateToUrl } from "single-spa";

/**
 * ---------------------------------------------------------
 * Route → MFE Mapping
 * ---------------------------------------------------------
 * Maps URL prefixes to possible MFEs.
 * Order matters (first match wins).
 */
const routeAppMap: Record<string, string[]> = {
  '/accounts': ['marketing', 'marketing_legacy'],
  '/service': ['service', 'service_legacy'],
  '/commerce': ['maropost_commerce']
}

/**
 * ---------------------------------------------------------
 * App → Preview Mapping
 * ---------------------------------------------------------
 * Maps MFE app names to Preview page names.
 * This is used ONLY for redirecting to preview page.
 */
const appPreviewMap: Record<string, string> = {
  marketing: 'Marketing',
  marketing_legacy: 'Marketing',
  service: 'Service',
  service_legacy: 'Service',
  maropost_commerce: 'Commerce'
}

/**
 * ---------------------------------------------------------
 * Types
 * ---------------------------------------------------------
 */

/**
 * Access configuration for a single route
 */
export interface AccessRoute {
  path: string
  feature?: string | string[]
  role?: string | string[]
}

/**
 * User store shape required for access check
 */
export interface UserStore {
  features: string[]
  roles: string[]
}

/**
 * ---------------------------------------------------------
 * Resolve MFEs from route
 * ---------------------------------------------------------
 * Returns list of possible MFEs for a given path.
 */
function getAppsForRoute(path: string): string[] {
  for (const prefix in routeAppMap) {
    if (path.startsWith(prefix)) {
      return routeAppMap[prefix]
    }
  }
  return []
}

/**
 * ---------------------------------------------------------
 * Route Matching Helper
 * ---------------------------------------------------------
 * Uses path-to-regexp to match dynamic routes like:
 * /:accountId/settings/:id
 */
function matchRoute(path: string, routePath: string): boolean {
  try {
    return !!match(routePath, { decode: decodeURIComponent })(path)
  } catch {
    return false
  }
}

/**
 * ---------------------------------------------------------
 * Normalize Path
 * ---------------------------------------------------------
 * Removes product prefix from path
 * Example:
 * /accounts/123/settings → /123/settings
 */
function normalizePath(path: string): string {
  for (const prefix in routeAppMap) {
    if (path.startsWith(prefix)) {
      return path.replace(prefix, '') || '/'
    }
  }
  return path
}

/**
 * ---------------------------------------------------------
 * Core Access Check Function
 * ---------------------------------------------------------
 *
 * @param path - current route path (e.g. /accounts/123/settings)
 * @param userStore - contains user features and roles
 *
 * @returns boolean → true = allow, false = block
 *
 * Flow:
 * 1. Resolve possible MFEs from path
 * 2. Find matching route in accessMapCache
 * 3. If no config → allow
 * 4. Validate feature
 * 5. Validate role
 */
export function canAccessRoute(
  path: string,
  userStore: ReturnType<typeof useUserStore>
): boolean {
  let matchedRoute: AccessRoute | undefined
  let matchedAppName: string | undefined // 👈 track which app matched

  // Step 1: Identify candidate MFEs
  const appNames = getAppsForRoute(path)

  // Step 2: Find matching route from cache
  for (const appName of appNames) {
    const allRoutes = accessMapCache[appName]

    // Skip if accessMap not loaded yet
    if (!allRoutes || !allRoutes.length) continue

    // Sort routes by specificity (longer paths first)
    const sortedRoutes = [...allRoutes].sort(
      (a, b) => b.path.length - a.path.length
    )

    const found = sortedRoutes.find((route) =>
      matchRoute(normalizePath(path), route.path)
    )

    if (found) {
      matchedRoute = found
      matchedAppName = appName // 👈 capture app name for preview redirect
      break
    }
  }

  // Step 3: No restriction → allow
  if (!matchedRoute) return true

  const { feature, role } = matchedRoute

  /**
   * ---------------------------------------------------------
   * Step 4: Feature validation
   * ---------------------------------------------------------
   */
  if (feature) {
    const requiredFeatures = Array.isArray(feature)
      ? feature
      : [feature]

    const features = userStore.getPackageFeatures || []

    const hasFeature = requiredFeatures.some((f) =>
      features.some((feature) => feature.feature_key ===f)
    )

    // 🚫 Feature not available → redirect to Preview page
    if (!hasFeature) {
      // Resolve preview app name dynamically
      const previewApp = appPreviewMap[matchedAppName || ''] || ''

      navigateToUrl(`/preview/${previewApp}`)
      return false
    }
  }

  /**
   * ---------------------------------------------------------
   * Step 5: Role validation
   * ---------------------------------------------------------
   */
  if (role) {
    const requiredRoles = Array.isArray(role)
      ? role
      : [role]

    const roles = userStore.getRoles || []

    const hasRole = requiredRoles.some((r) =>
      roles.includes(r)
    )

    // 🚫 Role not allowed → redirect to access denied
    if (!hasRole) {
      navigateToUrl('/access-denied')
      return false
    }
  }

  /**
   * ---------------------------------------------------------
   * Step 6: Access granted
   * ---------------------------------------------------------
   */
  return true
}